import React, { useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  USAGE_CATEGORY_LABELS,
  UsageBucketDTO,
  UsageCategory,
} from "../../../api/admin/types";
import {
  adjustWorkflowTotal,
  CATEGORY_COLORS,
  visibleCategories,
} from "../shared/constants";

interface TrendChartProps {
  buckets: UsageBucketDTO[];
  title?: string;
  subtitle?: string;
  showEpic?: boolean;
}

type Mode = "total" | "by_category";

const TrendChart: React.FC<TrendChartProps> = ({ buckets, title, subtitle, showEpic = true }) => {
  const [mode, setMode] = useState<Mode>("total");
  const [hidden, setHidden] = useState<Record<UsageCategory, boolean>>(
    {} as Record<UsageCategory, boolean>
  );

  const data = useMemo(
    () =>
      buckets.map((b) => ({
        label: b.label,
        // Subtract Epic from the displayed total when toggle is off so the
        // line on the chart matches the visible per-category cells.
        total: adjustWorkflowTotal(b.total, b.totals, showEpic),
        ...b.totals,
      })),
    [buckets, showEpic]
  );

  const allowed = visibleCategories(showEpic);
  const visibleCats = allowed.filter((c) => !hidden[c]);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 20,
        width: "100%",
        maxWidth: 1200,
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{title ?? "Total account usage health"}</div>
          {subtitle ? (
            <div style={{ fontSize: 12, color: "#6b7280" }}>{subtitle}</div>
          ) : null}
        </div>
        <div style={{ display: "inline-flex", background: "#f0f0f0", borderRadius: 6, padding: 3 }}>
          {(["total", "by_category"] as Mode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                padding: "5px 12px",
                fontSize: 12,
                fontWeight: 500,
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                background: mode === m ? "#3949ab" : "transparent",
                color: mode === m ? "#fff" : "#555",
              }}
            >
              {m === "total" ? "Total" : "By category"}
            </button>
          ))}
        </div>
      </div>

      <div style={{ width: "100%", height: 360 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 10, right: 24, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip />
            {mode === "total" ? (
              <Line
                type="monotone"
                dataKey="total"
                stroke="#3949ab"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                name="Total tasks"
              />
            ) : (
              <>
                <Legend
                  onClick={(e) => {
                    const k = e.dataKey as UsageCategory;
                    setHidden((h) => ({ ...h, [k]: !h[k] }));
                  }}
                  wrapperStyle={{ fontSize: 12 }}
                />
                {visibleCats.map((c) => (
                  <Line
                    key={c}
                    type="monotone"
                    dataKey={c}
                    name={USAGE_CATEGORY_LABELS[c]}
                    stroke={CATEGORY_COLORS[c]}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TrendChart;
